/* ==========================================================================
   Selected by - onze artisans

   De rail is native scroll-snap (CSS): swipe en toetsenbord werken zonder dit
   script. Het script voegt toe:
     - vorige/volgende-knoppen (vanaf 990px) en een voortgangsindicator
     - slepen met de muis, met traagheid (Draggable + InertiaPlugin), alleen
       met een fijne aanwijzer. Tijdens het slepen staat scroll-snap uit, bij
       loslaten landt de worp op een kaart en gaat snap weer aan.
     - kaarten die gestaffeld omhoog komen (ScrollTrigger met de juiste
       scroller)

   Bij mislukken of minder beweging blijft de rail een gewone scrollbare rail.
   Knoppen en voortgang hebben geen GSAP nodig en werken altijd.
   ========================================================================== */

(() => {
  if (customElements.get('selected-artisans')) return;

  /* Vanaf 990 px scrolt in dit thema .page-wrapper en niet het venster
     (base.css). ScrollTrigger moet dat element als scroller krijgen, anders
     reageert hij nooit. Onder 990 px scrolt het venster. */
  const kiesScroller = () => {
    const pagina = document.querySelector('.page-wrapper');
    return pagina && /(auto|scroll)/.test(getComputedStyle(pagina).overflowY) ? pagina : window;
  };

  class SelectedArtisans extends HTMLElement {
    connectedCallback() {
      if (this.gestart) return;
      this.gestart = true;
      this.opruimers = [];

      this.rail = this.querySelector('[data-sa-rail]');
      this.kaarten = Array.from(this.querySelectorAll('[data-sa-kaart]'));
      if (!this.rail || !this.kaarten.length) return;

      this.bediening();
      this.luisterNaarEditor();
      this.startBeweging();
    }

    disconnectedCallback() {
      this.opruimen();
    }

    opruimen() {
      this.gestart = false;
      this.token = null;
      (this.opruimers || []).forEach((f) => f());
      this.opruimers = [];
    }

    luisterNaarEditor() {
      const weg = (e) => {
        const sectieId = (this.closest('.shopify-section')?.id || '').replace('shopify-section-', '');
        if (e.detail?.sectionId && e.detail.sectionId === sectieId) this.opruimen();
      };
      document.addEventListener('shopify:section:unload', weg);
      this.opruimers.push(() => document.removeEventListener('shopify:section:unload', weg));
    }

    /* ----------------------------------------------------------------------
       Knoppen en voortgang (geen GSAP nodig)
       ---------------------------------------------------------------------- */
    bediening() {
      const { rail } = this;
      const paneel = this.querySelector('[data-sa-bediening]');
      const vorige = this.querySelector('[data-sa-vorige]');
      const volgende = this.querySelector('[data-sa-volgende]');
      const balk = this.querySelector('[data-sa-balk]');
      const traag = window.matchMedia('(prefers-reduced-motion: reduce)');

      const max = () => rail.scrollWidth - rail.clientWidth;

      const bijwerken = () => {
        const m = max();
        // Past alles in beeld, dan is er niets te bedienen
        if (paneel) paneel.hidden = m <= 2;
        const pos = rail.scrollLeft;
        const duim = Math.min(100, (rail.clientWidth / rail.scrollWidth) * 100);
        this.style.setProperty('--sa-duim', `${duim}%`);
        // translateX in procenten van de duim zelf: (100 - duim) / duim * voortgang
        const voortgang = m > 0 ? Math.min(1, Math.max(0, pos / m)) : 0;
        this.style.setProperty('--sa-pos', `${((100 - duim) / duim) * voortgang * 100}%`);
        if (vorige) vorige.disabled = pos <= 2;
        if (volgende) volgende.disabled = pos >= m - 2;
      };

      let raf = 0;
      const metRaf = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(bijwerken);
      };

      const stap = () => {
        const k = this.kaarten[0];
        return k ? k.getBoundingClientRect().width : rail.clientWidth * 0.8;
      };
      const loop = (richting) => () =>
        rail.scrollBy({ left: richting * stap(), behavior: traag.matches ? 'auto' : 'smooth' });
      const naarVorige = loop(-1);
      const naarVolgende = loop(1);

      rail.addEventListener('scroll', metRaf, { passive: true });
      vorige?.addEventListener('click', naarVorige);
      volgende?.addEventListener('click', naarVolgende);
      const ro = new ResizeObserver(metRaf);
      ro.observe(rail);
      bijwerken();

      this.opruimers.push(() => {
        cancelAnimationFrame(raf);
        rail.removeEventListener('scroll', metRaf);
        vorige?.removeEventListener('click', naarVorige);
        volgende?.removeEventListener('click', naarVolgende);
        ro.disconnect();
      });
    }

    /* ----------------------------------------------------------------------
       Slepen en reveal, met GSAP
       ---------------------------------------------------------------------- */
    async startBeweging() {
      if (!window.elGsap) return;
      const token = (this.token = {});

      let api;
      try {
        api = await window.elGsap.laad(this, ['ScrollTrigger']);
      } catch (fout) {
        return;
      }
      if (this.token !== token) return;

      let kanSleep = false;
      try {
        await window.elGsap.laad(this, ['Draggable', 'InertiaPlugin']);
        kanSleep = !!(window.Draggable && window.InertiaPlugin);
      } catch (fout) {
        kanSleep = false;
      }
      if (this.token !== token) return;

      const { gsap, ScrollTrigger } = api;
      const { rail, kaarten } = this;
      const animatie = this.hasAttribute('data-sa-animatie');

      let timer = null;
      const vernieuw = () => {
        clearTimeout(timer);
        timer = setTimeout(() => ScrollTrigger.refresh(), 150);
      };

      const mm = gsap.matchMedia();

      mm.add(
        {
          beweeg: '(prefers-reduced-motion: no-preference)',
          muis: '(hover: hover) and (pointer: fine)',
          groot: '(min-width: 990px)',
        },
        (ctx) => {
          const { beweeg, muis, groot } = ctx.conditions;
          const scroller = kiesScroller();
          const luisteraars = [];

          /* Reveal: kaarten stijgen gestaffeld in. Eerst de beginstand, dan
             afspelen bij in beeld (een from() met stagger zet niet alle
             beginstanden meteen). */
          if (beweeg && animatie) {
            gsap.set(kaarten, { opacity: 0, y: 40 });
            let gespeeld = false;
            const speel = () => {
              if (gespeeld) return;
              gespeeld = true;
              gsap.to(kaarten, {
                opacity: 1,
                y: 0,
                duration: 0.9,
                ease: 'power3.out',
                stagger: 0.1,
                clearProps: 'opacity,transform',
              });
            };
            ScrollTrigger.create({ scroller, trigger: rail, start: 'top 88%', onEnter: speel, onLeave: speel, onEnterBack: speel });
          }

          /* Slepen met de muis: een proxy draagt de worp, de rail scrolt mee.
             Alleen vanaf 990px met een fijne aanwijzer; op touch is de rail
             gewoon native scroll-snap. */
          if (kanSleep && muis && groot) {
            const { Draggable } = window;
            const proxy = document.createElement('div');
            let bewogen = 0;

            const posities = () => {
              const eerste = kaarten[0].offsetLeft;
              const m = rail.scrollWidth - rail.clientWidth;
              return kaarten.map((k) => Math.min(m, k.offsetLeft - eerste));
            };
            const dichtstbij = (v) => posities().reduce((b, p) => (Math.abs(p - v) < Math.abs(b - v) ? p : b), 0);

            const sleep = Draggable.create(proxy, {
              type: 'x',
              trigger: rail,
              inertia: true,
              edgeResistance: 0.85,
              minimumMovement: 5,
              snap: { x: (v) => -dichtstbij(-v) },
              onPress() {
                bewogen = 0;
                rail.style.scrollSnapType = 'none';
                rail.style.scrollBehavior = 'auto';
                const m = rail.scrollWidth - rail.clientWidth;
                gsap.set(proxy, { x: -rail.scrollLeft });
                this.update();
                this.applyBounds({ minX: -m, maxX: 0 });
              },
              onDragStart() {
                rail.classList.add('is-slepen');
              },
              onDrag() {
                bewogen = Math.max(bewogen, Math.abs(this.x + rail.scrollLeft));
                rail.scrollLeft = -this.x;
              },
              onThrowUpdate() {
                rail.scrollLeft = -this.x;
              },
              onRelease() {
                // Geen worp (zonder beweging of stilstaand losgelaten): direct terug naar snap
                if (!this.isThrowing) herstel();
              },
              onThrowComplete() {
                herstel();
              },
            })[0];

            const herstel = () => {
              rail.classList.remove('is-slepen');
              rail.style.scrollSnapType = '';
              rail.style.scrollBehavior = '';
            };

            // Na een sleepbeweging telt het loslaten niet als klik op een link
            const blokkeerKlik = (e) => {
              if (bewogen > 5) {
                e.preventDefault();
                e.stopPropagation();
                bewogen = 0;
              }
            };
            rail.addEventListener('click', blokkeerKlik, true);
            luisteraars.push(() => {
              rail.removeEventListener('click', blokkeerKlik, true);
              sleep.kill();
              herstel();
            });
          }

          vernieuw();
          return () => luisteraars.forEach((f) => f());
        }
      );

      this.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', vernieuw, { once: true });
      });
      window.addEventListener('load', vernieuw, { once: true });

      this.opruimers.push(() => {
        clearTimeout(timer);
        window.removeEventListener('load', vernieuw);
        mm.revert();
      });
    }
  }

  customElements.define('selected-artisans', SelectedArtisans);

  document.addEventListener('shopify:section:load', (e) => {
    e.target?.querySelectorAll?.('selected-artisans').forEach((el) => {
      if (!el.gestart) el.connectedCallback();
    });
  });
})();
